import { useCallback, useRef } from 'react';

import { type ActiveFiltersState } from '../../../shared/types';
import {
  type ViolatingEntitlements,
  type ViolationAssignedEntitlements,
} from '../data-layer/detailsTypes';
import { detailsActions } from '../data-store/detailsSlice';
import { getPayload } from '../utils';

const MAX_COUNT = 50;

const useConflictApiEndpoints = (
  activeFilters: ActiveFiltersState[],
  searchKey: string | null
) => {
  // Function API call
  const functionAbortRef = useRef<((reason: string) => void) | null>(null);
  const assignedEntAbortRef = useRef<((reason: string) => void) | null>(null);
  const violatingEntAbortRef = useRef<((reason: string) => void) | null>(null);

  const [
    fetchViolationFunctions,
    { isError: isFunctionError, isLoading: isFunctionLoading },
  ] = detailsActions.useGetViolationFunctionMutation();
  const [
    fetchAssignedEntitlements,
    {
      isError: isAssignedEntilementError,
      isLoading: isAssignedEntilementLoading,
    },
  ] = detailsActions.useGetAssignedEntitlementsMutation();
  const [
    fetchViolatingEntitlements,
    {
      isError: isViolatingEntilementError,
      isLoading: isViolatingEntilementLoading,
    },
  ] = detailsActions.useGetViolatingEntitlementsMutation();

  const handleFetchViolationFunctions = useCallback(
    async (violationId: string) => {
      // Map for the active options for different filters
      const activeFiltersMap = Object.fromEntries(
        activeFilters.map((f) => [f.value, f.options.map((o) => o.value)])
      );

      const payload = getPayload(activeFiltersMap, searchKey);

      functionAbortRef.current?.('New Search');

      try {
        const controller = fetchViolationFunctions({ violationId, payload });

        functionAbortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        return response;
      } catch {
        throw Error("Failed violation functions' API");
      }
    },
    [fetchViolationFunctions, activeFilters, searchKey]
  );

  const handleFetchAssignedEntitlements = useCallback(
    async (
      violationId: string,
      functionId: string,
      parameterKey: string | null,
      isOnExpand = false
    ) => {
      // Map for the active options for different filters
      const activeFiltersMap = Object.fromEntries(
        activeFilters.map((f) => [f.value, f.options.map((o) => o.value)])
      );

      const payload = getPayload(activeFiltersMap, searchKey);

      if (!isOnExpand) {
        assignedEntAbortRef.current?.('New Search');
      }

      try {
        const controller = fetchAssignedEntitlements({
          violationId,
          functionId,
          payload: {
            ...payload,
            afterKey: parameterKey,
            max: MAX_COUNT,
          },
        });

        assignedEntAbortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        const { data, afterKey } = response as {
          data: ViolationAssignedEntitlements[];
          afterKey: string | null;
        };

        return {
          data,
          currentAfterKey: afterKey,
        };
      } catch {
        throw Error('Failed assigned entitlement API');
      }
    },
    [fetchAssignedEntitlements, activeFilters, searchKey]
  );

  const handleFetchViolatingEntitlements = useCallback(
    async (
      violationId: string,
      functionId: string,
      assignedEntitlementId: string,
      parameterKey: string[] | null
    ) => {
      // Map for the active options for different filters
      const activeFiltersMap = Object.fromEntries(
        activeFilters.map((f) => [f.value, f.options.map((o) => o.value)])
      );

      const payload = getPayload(activeFiltersMap, searchKey);

      violatingEntAbortRef.current?.('New Search');

      try {
        const controller = fetchViolatingEntitlements({
          violationId,
          functionId,
          assignedEntitlementId,
          payload: {
            ...payload,
            searchAfter: parameterKey,
            max: MAX_COUNT,
          },
        });

        violatingEntAbortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        const { data, searchAfter } = response as {
          data: ViolatingEntitlements[];
          searchAfter: string[] | null;
        };

        return {
          data,
          currentAfterKey: searchAfter,
        };
      } catch {
        throw Error('Failed assigned entitlement API');
      }
    },
    [fetchViolatingEntitlements, activeFilters, searchKey]
  );

  return {
    handleFetchViolationFunctions,
    handleFetchAssignedEntitlements,
    handleFetchViolatingEntitlements,
    states: {
      isFunctionError,
      isFunctionLoading,
      isAssignedEntilementError,
      isAssignedEntilementLoading,
      isViolatingEntilementError,
      isViolatingEntilementLoading,
    },
  };
};

// eslint-disable-next-line import/prefer-default-export
export { useConflictApiEndpoints };
